// Pure helpers for the Parallel run page. Kinds match drata_snapshot.kind in packages/schema.

/** Consecutive passing days before sign-off. Mirrors STREAK_GOAL in packages/modules/src/parallel/exit.ts. */
export const STREAK_GOAL = 5

export const SNAPSHOT_KINDS = [
  'MONITOR_RESULT',
  'PERSONNEL_CHECK',
  'DEVICE_COMPLIANCE',
  'CONTROL_READINESS',
  'FRAMEWORK_READINESS',
  'POLICY_ACK_COUNT',
  'EVIDENCE_STATUS',
] as const

export const MISMATCH_STATUSES = ['OPEN', 'EXPLAINED', 'ACCEPTED', 'FIXED'] as const

const STATUS_LABEL: Record<string, string> = {
  OPEN: 'Open',
  EXPLAINED: 'Explained',
  ACCEPTED: 'Accepted',
  FIXED: 'Fixed',
}

export function mismatchStatusLabel(status: string): string {
  return STATUS_LABEL[status] ?? status
}

/** Matches MIN_ACCEPT_REASON and the explanation cap in packages/modules/src/parallel/rules.ts. */
export const ACCEPT_REASON_MIN = 10
export const ACCEPT_REASON_MAX = 4_000

export interface KindTotals {
  matched: number
  mismatched: number
  /** Missing on a run from before accepted differences were counted. Treat as 0. */
  accepted?: number
  notComparable: number
  matchPercent: number | null
}

export function acceptedCount(t: KindTotals | undefined): number {
  return t?.accepted ?? 0
}

export interface RunRow {
  id: string
  runDate: string
  byKind: Record<string, KindTotals>
  matched: number
  mismatched: number
  notComparable: number
  matchPercent: number | null
  readiness: ReadinessSide[]
  exitStatus: string
  exitDetail: ExitDetail
  consecutivePassingDays: number
}

export interface ReadinessSide {
  slug: string
  drataPercent: number | null
  clearpolPercent: number | null
  drataReadyControls: number | null
  clearpolReadyControls: number | null
  equal: boolean
}

export interface ExitDetail {
  frameworkReadinessEqual: boolean
  monitorMatchOk: boolean
  personnelMatchOk: boolean
  unexplainedOlderThan3Days: number
  unexplainedOk: boolean
  passingToday: boolean
  consecutivePassingDays: number
  streakComplete: boolean
}

export interface MismatchRow {
  id: string
  kind: string
  itemKey: string
  status: string
  firstSeenOn: string
  explanation?: string | null
  version?: number
  drataValue?: unknown
  clearpolValue?: unknown
}

export interface MismatchFilter {
  kind: string
  status: string
  minAge: number
}

export const EMPTY_FILTER: MismatchFilter = { kind: '', status: '', minAge: 0 }

export function ageDays(firstSeenOn: string, today: string): number {
  const a = Date.parse(`${firstSeenOn.slice(0, 10)}T00:00:00Z`)
  const b = Date.parse(`${today.slice(0, 10)}T00:00:00Z`)
  if (Number.isNaN(a) || Number.isNaN(b)) return 0
  return Math.round((b - a) / 86_400_000)
}

export function filterMismatches(rows: MismatchRow[], filter: MismatchFilter, today: string): MismatchRow[] {
  return rows.filter((row) => {
    if (filter.kind && row.kind !== filter.kind) return false
    if (filter.status && row.status !== filter.status) return false
    if (filter.minAge > 0 && ageDays(row.firstSeenOn, today) < filter.minAge) return false
    return true
  })
}

export interface TrendPoint {
  runDate: string
  matchPercent: number | null
  exitStatus: string
}

/** Last 60 days, oldest first, so a bar chart reads left to right. */
export function trendPoints(runs: Pick<RunRow, 'runDate' | 'matchPercent' | 'exitStatus'>[]): TrendPoint[] {
  return [...runs]
    .sort((a, b) => a.runDate.localeCompare(b.runDate))
    .slice(-60)
    .map((r) => ({ runDate: r.runDate.slice(0, 10), matchPercent: r.matchPercent, exitStatus: r.exitStatus }))
}

export interface CheckItem {
  label: string
  ok: boolean
  detail: string
}

export function exitChecklist(
  run: Pick<RunRow, 'exitDetail' | 'consecutivePassingDays'> | null,
): CheckItem[] {
  const d = run?.exitDetail
  const streak = run?.consecutivePassingDays ?? 0
  return [
    {
      label: 'Framework readiness equal',
      ok: !!d?.frameworkReadinessEqual,
      detail: d?.frameworkReadinessEqual ? 'Equal' : 'Not equal',
    },
    {
      label: 'Monitor results at least 98%',
      ok: !!d?.monitorMatchOk,
      detail: d?.monitorMatchOk ? 'Met' : 'Not met',
    },
    {
      label: 'Personnel checks at least 98%',
      ok: !!d?.personnelMatchOk,
      detail: d?.personnelMatchOk ? 'Met' : 'Not met',
    },
    {
      label: 'No unexplained mismatch older than 3 days',
      ok: !!d?.unexplainedOk,
      detail: `${d?.unexplainedOlderThan3Days ?? 0} older than 3 days`,
    },
    {
      label: `${STREAK_GOAL} consecutive passing days`,
      ok: !!d?.streakComplete,
      detail: `${streak} of ${STREAK_GOAL}`,
    },
  ]
}

/** The only field an admin or agent sends. The server sets EXPLAINED and explainedBy. */
export function explainPatch(text: string): { explanation: string } {
  return { explanation: text.trim() }
}

export function canSeeParallel(roles: readonly string[]): boolean {
  return roles.includes('ADMIN') || roles.includes('INFOSEC_LEAD')
}

/** Accept, and moving a row out of ACCEPTED, is the same two roles. The page still hides the control for anyone else. */
export function canAcceptMismatches(roles: readonly string[]): boolean {
  return roles.includes('ADMIN') || roles.includes('INFOSEC_LEAD')
}

export function toggleSelected(selected: readonly string[], id: string, on: boolean): string[] {
  if (on) return selected.includes(id) ? [...selected] : [...selected, id]
  return selected.filter((x) => x !== id)
}

/** Select or clear every id in the current filter. Ids outside that filter stay selected. */
export function setFilterSelection(
  selected: readonly string[],
  ids: readonly string[],
  on: boolean,
): string[] {
  const drop = new Set(ids)
  const rest = selected.filter((id) => !drop.has(id))
  return on ? [...rest, ...ids] : rest
}

export function selectionState(
  selected: readonly string[],
  ids: readonly string[],
): boolean | 'indeterminate' {
  if (ids.length === 0) return false
  const n = ids.filter((id) => selected.includes(id)).length
  if (n === 0) return false
  if (n === ids.length) return true
  return 'indeterminate'
}

export interface AcceptPayload {
  ids: string[]
  reason: string
}

/** Body for POST /api/v1/parallel/mismatches/accept. Throws when the selection or the reason is not usable. */
export function acceptPayload(ids: readonly string[], reason: string): AcceptPayload {
  const list = [...new Set(ids.map((id) => id.trim()).filter(Boolean))]
  const text = reason.trim()
  if (list.length < 1 || list.length > 500) throw new Error('Select 1 to 500 mismatches.')
  if (text.length < ACCEPT_REASON_MIN)
    throw new Error(`Reason must be at least ${ACCEPT_REASON_MIN} characters.`)
  if (text.length > ACCEPT_REASON_MAX)
    throw new Error(`Reason must be at most ${ACCEPT_REASON_MAX} characters.`)
  return { ids: list, reason: text }
}
